import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Grid, Box, Alert, Paper, Typography } from '@mui/material';
import ChatInterface, { Message } from '../../common/ChatInterface';
import StepHeader from '../../common/StepHeader';
import AgentGraph from './AgentGraph';
import ArchitectureSpecEditor from './ArchitectureSpecEditor';
import {
  useWorkflow,
  type ChatHistoryMessage,
} from '../../../contexts/WorkflowContext';
import {
  workflowService,
  type ChatResponse,
  type QualityReport,
  type ArchitectureOutput,
  type StepStructuredData,
} from '../../../services/workflow';
import type {
  FeedbackRating,
  MessageFeedbackState,
} from '../../../types/feedback';

// Props for step data loaded from backend (for resume functionality)
interface MultiAgentStepProps {
  initialChatHistory?: ChatHistoryMessage[];
  initialSpecData?: StepStructuredData | null;
  initialQualityScore?: number;
  initialQualityReport?: QualityReport;
}

// Default welcome message
const WELCOME_MESSAGE: Message = {
  id: '1',
  role: 'assistant',
  content:
    "I'll analyze your agent design to determine the best architecture. Let me assess whether a single agent or multi-agent system would be most effective for your requirements...",
  timestamp: Date.now(),
};

const MultiAgentStep: React.FC<MultiAgentStepProps> = ({
  initialChatHistory,
  initialSpecData,
  initialQualityScore,
  initialQualityReport,
}) => {
  const { projectId, learningMode } = useWorkflow();

  // Track which messages have received feedback
  const [messageFeedback, setMessageFeedback] = useState<MessageFeedbackState>(
    {}
  );

  const handleFeedbackSubmit = useCallback(
    (messageIndex: number, rating: FeedbackRating) => {
      setMessageFeedback((prev) => ({
        ...prev,
        [messageIndex]: rating,
      }));
    },
    []
  );

  // Check if we have loaded data from a previous session
  const hasLoadedData = Boolean(
    initialChatHistory && initialChatHistory.length > 0
  );

  // Convert loaded chat history to Message format, or use welcome message
  const initialMessages = useMemo<Message[]>(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      return initialChatHistory.map((msg, index) => ({
        id: `loaded-${index}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
      }));
    }
    return [WELCOME_MESSAGE];
  }, [initialChatHistory]);

  const [messages, setMessages] = useState<Message[]>(initialMessages);

  // Architecture state - initialize from loaded data
  const [architecture, setArchitecture] = useState<ArchitectureOutput | null>(
    () => {
      if (initialSpecData && 'is_multi_agent' in initialSpecData) {
        return initialSpecData as ArchitectureOutput;
      }
      return null;
    }
  );

  // Quality state - initialize from loaded data
  const [qualityScore, setQualityScore] = useState<number>(
    initialQualityScore ?? 0
  );
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(
    initialQualityReport ?? null
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialMessageSent = useRef(hasLoadedData); // Skip auto-trigger if we have loaded data

  // Update state when initial data changes
  useEffect(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      setMessages(
        initialChatHistory.map((msg, index) => ({
          id: `loaded-${index}`,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp).getTime(),
        }))
      );
      initialMessageSent.current = true;
    }
  }, [initialChatHistory]);

  useEffect(() => {
    if (initialSpecData && 'is_multi_agent' in initialSpecData) {
      setArchitecture(initialSpecData as ArchitectureOutput);
    }
  }, [initialSpecData]);

  useEffect(() => {
    if (initialQualityScore !== undefined) {
      setQualityScore(initialQualityScore);
    }
  }, [initialQualityScore]);

  useEffect(() => {
    if (initialQualityReport) {
      setQualityReport(initialQualityReport);
    }
  }, [initialQualityReport]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!projectId) {
        setError(
          'No project selected. Please create or select a project first.'
        );
        return;
      }

      const newMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, newMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const response: ChatResponse = await workflowService.chat(
          projectId,
          content,
          learningMode
        );

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Update architecture from structured_data if present (type guard for ArchitectureOutput)
        if (
          response.structured_data &&
          'is_multi_agent' in response.structured_data
        ) {
          const arch = response.structured_data as ArchitectureOutput;
          setArchitecture(arch);
        }

        // Update quality score
        if (response.quality_score !== undefined) {
          setQualityScore(response.quality_score);
        }

        // Update quality report
        if (response.quality_report) {
          setQualityReport(response.quality_report);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        console.error('Chat error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, learningMode]
  );

  // Send initial message to trigger architecture analysis when component mounts
  useEffect(() => {
    if (projectId && !initialMessageSent.current) {
      initialMessageSent.current = true;
      // Send a trigger message to the agent to analyze the design
      handleSendMessage(
        'Please analyze my agent design and recommend the appropriate architecture.'
      );
    }
  }, [projectId, handleSendMessage]);

  // Calculate quality score as percentage
  const qualityPercent = Math.round(qualityScore * 100);

  return (
    <>
      <StepHeader step="multiagent" />
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left Column: Chat */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            height="calc(100vh - 250px)"
            enableFeedback={!!projectId}
            projectId={projectId || undefined}
            step="multiagent"
            messageFeedback={messageFeedback}
            onFeedbackSubmit={handleFeedbackSubmit}
          />
        </Grid>

        {/* Right Column: Architecture Spec & Graph */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              height: 'calc(100vh - 250px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Scrollable content area for spec editor and graph */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
              {/* Architecture Spec Editor */}
              <ArchitectureSpecEditor
                architecture={architecture}
                qualityScore={qualityScore}
                qualityScoreLabel="Completeness"
                isLoading={isLoading}
              />

              {/* Agent Graph Visualization */}
              <Box sx={{ mt: 2 }}>
                <AgentGraph
                  diagramData={architecture?.diagram_data}
                  orchestrationPattern={architecture?.orchestration_pattern}
                />
              </Box>
            </Box>

            {/* Quality indicator - styled based on quality score */}
            <Paper
              sx={{
                p: 2,
                flexShrink: 0,
                bgcolor:
                  qualityPercent >= 80
                    ? 'success.light'
                    : qualityPercent >= 50
                      ? 'warning.light'
                      : 'grey.100',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {qualityPercent >= 80
                  ? 'Your architecture is ready for technical specification.'
                  : qualityPercent >= 50
                    ? 'Your architecture is taking shape. You can proceed or continue refining.'
                    : architecture
                      ? 'Keep iterating to finalize your architecture, or proceed when ready.'
                      : 'Chat with the agent to generate an architecture recommendation.'}
              </Typography>
              {qualityReport &&
                qualityReport.issues &&
                qualityReport.issues.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    Suggestions: {qualityReport.issues.slice(0, 2).join(', ')}
                  </Typography>
                )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default MultiAgentStep;
